// compressedUpload.js - smaller uploads when saving projects.
//
// Browsers never compress what they upload, so a 50 MB project used to travel as 50 MB on every
// save (also auto-save). Here the JSON is gzip-compressed in the browser first (CompressionStream,
// built into all current browsers) and sent with "Content-Encoding: gzip"; the API unpacks it
// (Services/GzipRequestMiddleware.cs). Project JSON usually shrinks 5-10x.
//
// Safe fallbacks:
//  - small bodies and browsers without CompressionStream: sent as before;
//  - an API that does not accept gzip yet (answers 400/415): the save is sent again uncompressed
//    and compression stays off until the page is reloaded.

import axios from "axios";

const MIN_BYTES_TO_COMPRESS = 64 * 1024;
let serverAcceptsGzip = true;

function canCompress() {
  return typeof window !== "undefined" && typeof window.CompressionStream === "function" && typeof Response === "function";
}

async function gzip(text) {
  const stream = new Blob([text]).stream().pipeThrough(new window.CompressionStream("gzip"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Drop-in for axios.post(url, body, { headers }) with a JSON body, compressed when worthwhile.
 * `body` may be an object or an already serialized JSON string. Resolves/rejects like axios.
 */
export async function postJson(url, body, config = {}) {
  const text = typeof body === "string" ? body : JSON.stringify(body);
  const jsonHeaders = { ...(config.headers || {}), "Content-Type": "application/json" };

  if (!serverAcceptsGzip || text.length < MIN_BYTES_TO_COMPRESS || !canCompress()) {
    return axios.post(url, text, { headers: jsonHeaders });
  }

  let packed;
  try {
    packed = await gzip(text);
  } catch (e) {
    console.log("Compression failed, saving uncompressed.", e);
    return axios.post(url, text, { headers: jsonHeaders });
  }

  try {
    return await axios.post(url, packed, {
      headers: { ...jsonHeaders, "Content-Encoding": "gzip" },
      transformRequest: [(data) => data], // send the bytes as they are
    });
  } catch (error) {
    const status = error && error.response && error.response.status;
    if (status === 400 || status === 415) {
      // Probably an API without gzip support: send it the old way, and keep doing that.
      serverAcceptsGzip = false;
      console.log("Server did not accept a compressed save; sending uncompressed.");
      return axios.post(url, text, { headers: jsonHeaders });
    }
    throw error;
  }
}

/**
 * Sends `text` as the form field `fieldName` (multipart/form-data), like posting a FormData with one
 * field, but gzip-compressed when worthwhile. Used by Publish (api/Export/CallForExport, field
 * "value"). Same fallbacks as postJson: small texts, browsers without CompressionStream and servers
 * that do not accept gzip get the old, uncompressed form.
 */
export async function postFormField(url, fieldName, text, config = {}) {
  const headers = { ...(config.headers || {}) };
  delete headers["Content-Type"];
  const plain = () => {
    const form = new FormData();
    form.append(fieldName, text);
    return axios.post(url, form, { headers });
  };
  if (!serverAcceptsGzip || text.length < MIN_BYTES_TO_COMPRESS || !canCompress()) return plain();

  // The same body the browser would build for a FormData, written out so it can be compressed.
  const boundary = "----toons" + Math.random().toString(16).slice(2) + Date.now().toString(16);
  const body =
    `--${boundary}\r\nContent-Disposition: form-data; name="${fieldName}"\r\n\r\n` +
    text +
    `\r\n--${boundary}--\r\n`;
  let packed;
  try {
    packed = await gzip(body);
  } catch (e) {
    return plain();
  }
  try {
    return await axios.post(url, packed, {
      headers: { ...headers, "Content-Type": `multipart/form-data; boundary=${boundary}`, "Content-Encoding": "gzip" },
      transformRequest: [(data) => data],
    });
  } catch (error) {
    const status = error && error.response && error.response.status;
    if (status === 400 || status === 415) {
      serverAcceptsGzip = false;
      console.log("Server did not accept a compressed upload; sending uncompressed.");
      return plain();
    }
    throw error;
  }
}
